/**
 * Navigation surface — single source of truth for the app's sections.
 *
 * Lifted from AppShell.jsx as part of Jitterbug Phase 1 (2026-06-01). The
 * SECTIONS array and the gating logic in `getVisibleSections` are
 * byte-equivalent in behavior to what AppShell used to do inline. No
 * gating policy changed during the extraction.
 *
 * SECTIONS describes the rail/flyout structure. Each section has:
 *   - key:              stable identifier (used for icon lookup, active-state matching)
 *   - label:            human-readable name
 *   - directNavigate:   if true, clicking the rail icon navigates directly to `route`
 *                       (no flyout). Used for placeholder / single-page sections.
 *   - route:            navigation target for directNavigate sections
 *   - routes:           VESTIGIAL. Declared on most sections and read by NOTHING
 *                       (verified repo-wide at S5.7). Breadcrumbs come from the
 *                       ROUTE_BREADCRUMBS table in AppShell; the active rail
 *                       section comes from the pathname-prefix ladder in
 *                       AppShell's section-key resolver. Neither consults this
 *                       array. Kept as intent documentation, in the same spirit
 *                       as supplierHidden below. Both are named here rather than
 *                       cited by line number — a line number is what made the
 *                       previous claim on this key wrong.
 *   - items:            for flyout sections, array of { label, route, ... } items
 *   - adminOnly:        section is hidden from non-ADMIN users
 *
 * Item-level flags:
 *   - requiresDisposalAccess: item is hidden from users without disposal access
 *                             (gate = DISPOSAL_ROLES in disposalRoles.js)
 *   - requiresFieldAccess:    item is hidden from users without field access
 *                             (gate = FIELD_ROLES in fieldRoles.js)
 *   - supplierHidden:         VESTIGIAL. No item declares it today; the
 *                             selector intentionally does not honor it.
 *                             Reserved for a future supplier-experience sprint.
 *
 * Items repointed at /field never render as active: AppShell does not mount at
 * that pathname (App.jsx returns the field zone ahead of the shell), so the
 * item-path comparison that draws the highlight never runs. Expected, not a
 * defect.
 */

import { hasDisposalAccess } from '../auth/disposalRoles';
import { hasFieldAccess } from '../auth/fieldRoles';

// S5.7 (2026-09-01): the three 'Field Inventory' items were repointed at /field,
// and the legacy /digitization, /disposal and /lifecycle inventory routes were
// deliberately left live in App.jsx — they still answer by URL and bookmark.
// B-263 (2026-11-02): Lifecycle Review's item was pointed back at
// /lifecycle/inventory, the supplier-facing capture screen; Digitization's and
// Disposal's stay at /field. All three keep requiresFieldAccess.

export const SECTIONS = [
  {
    key: 'digitization',
    label: 'Digitization',
    route: '/digitization',          // inert for the rail; consumed by the launcher tile
    routes: ['/digitization'],       // active-state owns the whole /digitization/* subtree
    launcherSingleTile: true,        // NEW flag
    items: [
      { label: 'Asset Registry',  route: '/digitization/registry' },
      { label: 'Field Inventory', route: '/field', requiresFieldAccess: true },
    ],
  },
  {
    key: 'disposal',
    label: 'Disposal Review',
    routes: ['/registry', '/disposal'],
    items: [
      { label: 'Asset Registry',   route: '/registry' },
      { label: 'Field Inventory',  route: '/field', requiresFieldAccess: true },
      { label: 'Disposal Recon',   route: '/disposal/recon' },
      { label: 'Disposal Shipments', route: '/supplier/disposal', requiresDisposalAccess: true },
    ],
  },
  {
    key: 'lifecycle',
    label: 'Lifecycle Review',
    routes: ['/registry', '/lifecycle'],
    items: [
      { label: 'Asset Registry',   route: '/registry' },
      { label: 'Field Inventory',  route: '/lifecycle/inventory', requiresFieldAccess: true },
      { label: 'Lifecycle Recon',  route: '/lifecycle/recon' },
    ],
  },
  {
    key: 'moves',
    label: 'Tool Moves',
    directNavigate: true,
    route: '/moves',
    routes: ['/moves'],
  },
  {
    // Warehouse — flyout over the /warehouse/* subtree. Visible in the rail to
    // all; each page role-gates to ADMIN/FIELD_REP (digitization Lock precedent).
    // Was a directNavigate single-tile straight to Stored Items; converted to a
    // flyout when Contracts (Phase 1) added a second destination. The rail icon
    // and active-state ('warehouse' for the whole /warehouse subtree) are
    // unchanged; the /warehouse → /warehouse/items redirect still stands.
    key: 'warehouse',
    label: 'Warehouse',
    routes: ['/warehouse'],
    items: [
      { label: 'Stored Items', route: '/warehouse/items' },
      { label: 'Contracts',    route: '/warehouse/contracts' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    routes: ['/admin'],
    adminOnly: true,
    items: [
      { label: 'List Assignment',  route: '/admin/list-assignment' },
      { label: 'Users',            route: '/admin/user-management' },
      { label: 'Sites',            route: '/admin/site-management' },
      { label: 'Projects',         route: '/admin/projects' },
      { label: 'Data Operations',  route: '/admin/data-ops' },
      { label: 'Data Exchange',    route: '/admin/data' },
      { label: 'Scrap Projects',   route: '/admin/disposal' },
      { label: 'Dispositions',     route: '/admin/dispositions' },
    ],
  },
];

/**
 * Return the visible subset of SECTIONS for a given role.
 *
 * Lifted UNCHANGED from the inline gating that lived in AppShell.jsx prior
 * to Jitterbug Phase 1 (refs were :140-141, :277, :290-300, :317-322):
 *   - section.adminOnly:           shown only when role === 'ADMIN'
 *   - item.requiresDisposalAccess: shown only when hasDisposalAccess(role)
 *   - item.supplierHidden:         NOT honored (vestigial; no item declares it)
 *
 * Plus one flag added at S5.7 (2026-09-01), NOT part of that lift:
 *   - item.requiresFieldAccess:    shown only when hasFieldAccess(role) — the
 *                                  same predicate that gated the deleted 'field'
 *                                  section, reused rather than rewritten.
 *
 * Sections without `items` (directNavigate sections like Digitization and
 * Tool Moves) pass through untouched. Sections with `items` are returned
 * with a filtered `items` array. The section objects themselves are
 * shallow-copied so callers can iterate without mutating the source.
 *
 * @param {{ role: string | undefined }} params
 * @returns {Array} role-filtered sections
 */
export function getVisibleSections({ role }) {
  const isAdmin = role === 'ADMIN';
  const userHasDisposalAccess = hasDisposalAccess(role);
  const userHasFieldAccess = hasFieldAccess(role);

  return SECTIONS
    .filter(section => !section.adminOnly || isAdmin)
    .map(section => {
      if (!Array.isArray(section.items)) return section;
      return {
        ...section,
        items: section.items.filter(item => {
          if (item.requiresDisposalAccess && !userHasDisposalAccess) return false;
          if (item.requiresFieldAccess && !userHasFieldAccess) return false;
          return true;
        }),
      };
    });
}
